import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Core } from '../_core/core';
import { IBuilding } from '../core.interface';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { MessageModule } from 'primeng/message';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

@Component({
  selector: 'app-buildings',
  standalone: true,
  imports: [
    ButtonModule,
    LucideAngularModule,
    MessageModule,
    CommonModule,
    FormsModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [DialogService],
  templateUrl: './buildings.html',
  styleUrl: './buildings.scss',
})
export class Buildings implements OnInit {
  private $core = inject(Core);
  private $dialog = inject(DialogService);
  private router = inject(Router);

  public buildingList = signal<IBuilding[]>([]);
  public searchQuery = signal('');
  public isLoading = signal<boolean>(false);

  public filteredBuildingList = computed(() => {
    const query = this.normalizeText(this.searchQuery());
    if (!query) {
      return this.buildingList();
    }
    return this.buildingList().filter(
      (building) =>
        this.normalizeText(building.tenantAddress).includes(query) ||
        this.normalizeText(building.tenantCity).includes(query) ||
        building.suppliers.some((supply) => this.normalizeText(supply.name).includes(query)),
    );
  });

  public selectedBuilding = this.$core.selectedBuilding();

  ngOnInit(): void {
    this.getBuildings();
  }

  private async getBuildings(): Promise<void> {
    this.isLoading.set(true);
    try {
      const data = await this.$core.fetchBuildings();
      this.buildingList.set(data);
    } finally {
      this.isLoading.set(false);
    }
  }

  public selectBuilding(building: IBuilding): void {
    this.$core.selectBuilding(building);
    if (building) {
      this.$core.selectBuilding(building);
      this.router.navigate(['/', 'loading']);
    }
  }

  public openAddBuilding(): void {
    this.router.navigate(['/propiedades-añadir']);
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }

  public getSupplyStyle(id: number) {
    const config: any = {
      1: { color: '#0D47A1', bg: 'rgba(13, 71, 161, 0.1)' },
      2: { color: '#D32F2F', bg: 'rgba(211, 47, 47, 0.1)' },
      3: { color: '#FB8C00', bg: 'rgba(251, 140, 0, 0.1)' },
      4: { color: '#00BCD4', bg: 'rgba(0, 188, 212, 0.1)' },
      5: { color: '#E65100', bg: 'rgba(230, 81, 0, 0.1)' },
      13: { color: '#FFB300', bg: 'rgba(255, 179, 0, 0.1)' },
      15: { color: '#FFB300', bg: 'rgba(255, 179, 0, 0.1)' },
    };
    const item = config[id] || { color: '#6c757d', bg: 'rgba(108, 117, 125, 0.1)' };
    return { color: item.color, 'background-color': item.bg };
  }

  public getSupplyIcon(id: number): string {
    const icons: any = {
      1: 'droplet',
      2: 'droplet',
      3: 'sun',
      4: 'snowflake',
      5: 'snowflake',
      13: 'snowflake',
      15: 'snowflake',
    };
    return icons[id] || 'archive';
  }
}
